import {ToastrService} from 'ngx-toastr';
import {
    comparator,
    composeComparators,
    filterNulls,
    HiddenValueWhileLoading,
    switchMapWithLoading,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, signal} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {combineLatest, filter, map, Observable, startWith, Subject, switchMap, take} from 'rxjs';
import {Sorting} from '@brunata-angular/ui';
import {ConsumptionAgentService} from '../consumption-agent.service';
import {ConsumptionCardViewComponent} from './consumption-card-view/consumption-card-view.component';
import {ConsumptionAgentTableComponent} from './consumption-agent-table/consumption-agent-table.component';
import {ConfirmationDialogService} from '@brunata-angular/ui';
import {Store} from '@ngrx/store';
import {selectBuilding} from 'src/app/state/selectors';
import {ConsumptionAgentTranslated} from './consumption-agent.model';
import {EditAgentEvent} from './consumption-agent-table/consumption-agent-overview.model';
import {MonitoringMeterBase} from '../../monitoring-meter.model';
import {DateTime} from 'luxon';

type ConsumptionAgentOverviewView = {
    buildingNo: string;
    consumptionAgents: HiddenValueWhileLoading<ConsumptionAgentTranslated[]>;
    sorting: Sorting;
    noActivatedChildren: boolean;
    validMeters: MonitoringMeterBase[];
};

@Component({
    selector: 'app-consumption-agents-overview',
    standalone: true,
    imports: [
        CommonModule,
        TranslatePipe,
        ConsumptionAgentTableComponent,
        ConsumptionCardViewComponent,
        ConsumptionCardViewComponent,
        RouterOutlet,
    ],
    templateUrl: './consumption-agents-overview.component.html',
    styleUrl: './consumption-agents-overview.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsumptionAgentsOverviewComponent {
    private readonly store = inject(Store);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly consumptionAgentService = inject(ConsumptionAgentService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly toastrService = inject(ToastrService);
    private readonly translateService = inject(TranslateService);
    private readonly refreshConsumptionAgentsTrigger$ = new Subject<void>();

    private readonly buildingNo$: Observable<string> = this.store.select(selectBuilding).pipe(
        filter(buildingState => buildingState.status === 'success'),
        map(building => building.buildingNo),
    );

    private readonly noActivatedChildren$: Observable<boolean> = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.activatedRoute.children.length === 0),
    );

    private readonly consumptionAgents$: Observable<
        HiddenValueWhileLoading<ConsumptionAgentTranslated[]>
    > = combineLatest([
        this.buildingNo$,
        this.noActivatedChildren$.pipe(filter(value => value)),
        this.refreshConsumptionAgentsTrigger$.pipe(startWith(null)),
    ]).pipe(
        switchMapWithLoading(([buildingNo]) =>
            this.consumptionAgentService.getConsumptionAgents(buildingNo).pipe(
                this.consumptionAgentService.addTranslationsForAgents(),
                map(consumptionAgents =>
                    consumptionAgents.sort(
                        composeComparators([
                            comparator({
                                selector: agent => agent.agentName,
                                order: 'asc',
                            }),
                            comparator({
                                selector: agent => agent.createdDate,
                                order: 'asc',
                            }),
                        ]),
                    ),
                ),
            ),
        ),
    );

    private readonly validMeters$: Observable<MonitoringMeterBase[]> = this.buildingNo$.pipe(
        switchMap(buildingNo => this.consumptionAgentService.getMetersForAgent(buildingNo)),
        map(meters => {
            const twoYearsAgo = DateTime.now().minus({years: 2});
            return meters.filter(meter => {
                if (meter.dismountedDate === null) {
                    return true;
                }
                return meter.dismountedDate > twoYearsAgo;
            });
        }),
    );

    readonly viewMode = signal<'table' | 'card'>('table');

    readonly view$: Observable<ConsumptionAgentOverviewView> = combineLatest({
        noActivatedChildren: this.noActivatedChildren$,
        buildingNo: this.buildingNo$,
        consumptionAgents: this.consumptionAgents$,
        sorting: this.consumptionAgentService.sorting.pipe(filterNulls()),
        validMeters: this.validMeters$,
    });

    private showNoMetersForEditDialog(buildingNo: string, event: EditAgentEvent): void {
        void this.confirmationDialogService
            .confirm(
                this.translateService.translate('monitoring_no_supported_meters'),
                this.translateService.translate('monitoring_edit_no_meters_warning'),
                this.translateService.translate('common_Delete'),
                this.translateService.translate('common_cancel_button'),
            )
            .then(confirmed => {
                if (confirmed) {
                    this.deleteAgent(buildingNo, event.reportId, event.agentName);
                }
            });
    }

    editAgent(event: EditAgentEvent, buildingNo: string, validMeters: MonitoringMeterBase[]): void {
        if (validMeters.length === 0) {
            this.showNoMetersForEditDialog(buildingNo, event);
            return;
        }

        void this.router.navigate([`${event.reportId}/edit`], {relativeTo: this.activatedRoute});
    }

    deleteAgent(buildingNo: string, reportId: string, agentName: string): void {
        void this.confirmationDialogService
            .confirm(
                this.translateService.translate('common_warning', 'online', 'common'),
                this.translateService
                    .translate('monitoring_agent_delete_warning')
                    .replace('{0}', agentName),
            )
            .then(confirmed => {
                if (confirmed === false) {
                    return;
                }
                this.consumptionAgentService
                    .deleteConsumptionAgent(buildingNo, reportId)
                    .subscribe(() => {
                        this.refreshConsumptionAgentsTrigger$.next();

                        this.toastrService.success(
                            this.translateService
                                .translate('monitoring_delete_consumption_agent_success')
                                .replace('{0}', agentName),
                        );
                    });
            });
    }

    duplicateAgent(buildingNo: string, agentReportId: string): void {
        this.consumptionAgentService
            .getConsumptionAgent(buildingNo, agentReportId)
            .pipe(
                take(1),
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                switchMap(({reportId, createdDate, ...agent}) =>
                    this.consumptionAgentService.createAgent(buildingNo, {
                        ...agent,
                        agentName: `${agent.agentName} - ${this.translateService.translate(`utilities_Copy_key`)}`,
                        meterSettings: {
                            ...agent.meterSettings,
                            meterGroup2:
                                agent.consumptionToMonitor === 'DIFFERENCECONSUMPTION'
                                    ? agent.meterSettings.meterGroup2
                                    : undefined,
                            meterSuperTypeNo: Number(agent.meterSettings.meterSuperTypeNo),
                            outputUnit: Number(agent.meterSettings.outputUnit),
                            usageAndUnits: agent.meterSettings.usageAndUnits.map(
                                ({alloUnitType, unit}) => ({
                                    alloUnitType,
                                    unit: Number(unit),
                                }),
                            ),
                        },
                    }),
                ),
            )
            .subscribe(({agentName, reportId}) => {
                void this.router
                    .navigate([reportId + '/edit'], {
                        relativeTo: this.activatedRoute,
                    })
                    .then(() => {
                        const successMessage = this.translateService
                            .translate('monitoring_agent_created')
                            .replace('{0}', agentName);
                        this.toastrService.success(successMessage);
                    });
            });
    }

    handleCreateClick(validMeters: MonitoringMeterBase[]): void {
        if (validMeters.length === 0) {
            this.showNoMetersForCreateDialog();
            return;
        }

        void this.router.navigate(['create'], {relativeTo: this.activatedRoute});
    }

    private showNoMetersForCreateDialog(): void {
        this.confirmationDialogService
            .confirmWithSettings({
                title: this.translateService.translate('monitoring_no_supported_meters'),
                message: this.translateService.translate('monitoring_no_meters_available'),
                showCancel: false,
            })
            .catch(error => {
                console.error('Dialog error:', error);
            });
    }
}
